/* eslint-disable wrap-iife */
import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import { usePhotoSeriesStore } from '../../../store/root.hook';
import { getPhotoSeries } from 'redux/photoseries/photoseries.selectors';
import { GenericError, VerificationError } from '../../../store/root';
import { ErrorModal, ErrorModalProps } from '../../../containers/error-modal/error-modal.component';
import { ImageTypeSettings, IMAGE_VERIFICATION_ERROR_TYPE } from 'redux/workflows/workflows';
import { VinModal } from 'components/pages/photoseries/containers/vin-modal/vin-modal.component';
import { imageVerificationErrors } from 'utils/constants.util';

type Props = {
  currentImageType: number;
  genericError: GenericError | null;
  shownVerificationErrorModal: boolean;
  noVinError: VerificationError | null | undefined;
  onContinue?: React.MouseEventHandler<HTMLButtonElement>;
  onRetake: React.MouseEventHandler<HTMLButtonElement>;
  retakeCount: number;
};

export function AugmentedModal(props: Props) {
  const {
    currentImageType,
    noVinError,
    genericError,
    shownVerificationErrorModal,
    onContinue,
    onRetake,
    retakeCount
  } = props;
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const {
    captureImage: { imageResponse, currentErrorCount },
    camera: { mode: cameraMode }
  } = usePhotoSeriesStore();

  if (genericError) {
    return (
      <ErrorModal
        shownModal
        onRetake={() => {
          genericError?.onAction();
        }}
        errors={genericError?.errors!}
        cameraMode={cameraMode}
        imageType={currentImageType}
      />
    );
  }

  if (!shownVerificationErrorModal) return null;

  const { enabledVerificationErrors } = _.find(photoSeries.imageTypeSettings, ({ imageType }) => imageType === currentImageType) as ImageTypeSettings;
  const blockerErrors = enabledVerificationErrors?.reduce((acc: number[], curr) => {
    if (curr?.errorType === IMAGE_VERIFICATION_ERROR_TYPE.BLOCKER) {
      acc.push(curr.error);
    }

    return acc;
  }, []);

  function mustRetakePhoto() {
    if (blockerErrors?.length === 0) return currentErrorCount < 2;

    const verificationErrors = imageResponse?.verificationErrors || [];

    for (let index = 0; index < verificationErrors!.length; index++) {
      const error = verificationErrors && verificationErrors[index].error;

      if (blockerErrors!.indexOf(error!) > -1) return true;
    }

    return currentErrorCount < 2;
  }

  const hasPortraitImageError = imageResponse?.verificationErrors?.some((e) => e.error === imageVerificationErrors.portraitImage);
  const _mustRetakePhoto = mustRetakePhoto() || hasPortraitImageError;

  // Check if there's an invalidVin error set as WARNING
  const hasInvalidVinWarning = (() => {
    const verificationErrors = imageResponse?.verificationErrors || [];
    const invalidVinError = verificationErrors.find((e) => e.error === imageVerificationErrors.invalidVin);

    if (!invalidVinError) return false;

    const enabledError = enabledVerificationErrors?.find((e) => e && e.error === imageVerificationErrors.invalidVin);
    return enabledError?.errorType === IMAGE_VERIFICATION_ERROR_TYPE.WARNING;
  })();

  const showVinModal = retakeCount > 0 && hasInvalidVinWarning;

  if (showVinModal) {
    return (
      <VinModal
        onContinue={onContinue}
        onRetake={onRetake}
        shownModal
      />
    );
  }

  const verificationErrorModalProps: ErrorModalProps = {
    shownModal: true,
    isBlocker: !!blockerErrors?.length,
    vehicle: imageResponse?.vehicle!,
    errors: imageResponse?.verificationErrors!,
    isVinError: Boolean(noVinError),
    showContinueButton: !_mustRetakePhoto,
    enabledVerificationErrors,
    onContinue,
    onRetake,
    cameraMode,
    imageType: currentImageType
  };

  return <ErrorModal {...verificationErrorModalProps} />;
}
